/**
 * Resolver el componente de una seccion sin acoplar el routing a los archivos
 * de pantalla.
 *
 * El problema
 * -----------
 * Las rutas de las secciones pendientes (`/productos`, `/facturacion`,
 * `/publicaciones`, `/pricing`) tienen que estar registradas HOY, pero sus
 * pantallas se escriben despues, en `src/screens/`. Un `import` estatico de un
 * archivo que todavia no existe rompe el build; un import dinamico a un path
 * variable obliga a que exista igual.
 *
 * La solucion
 * -----------
 * `import.meta.glob` le pide a Vite el mapa de TODO lo que hay en
 * `src/screens/` **en tiempo de build**. Si el archivo esta, la clave existe y
 * se carga por import dinamico (chunk aparte, lazy de verdad). Si no esta, la
 * clave simplemente no existe y se monta el cartel de "en construccion".
 *
 * Consecuencia deliberada: **agregar una pantalla es crear un archivo**. Nadie
 * tiene que editar el routing, el layout ni la navegacion para que aparezca, lo
 * cual importa porque estos archivos y los de `src/screens/` los tocan personas
 * distintas.
 *
 * Contrato que tiene que cumplir el archivo nuevo:
 *   1. Llamarse `src/screens/<Module>.tsx`, con el `<Module>` que declara la
 *      seccion en `routes.tsx`.
 *   2. Exportar el componente como `export function <Module>()` o como
 *      `export default`.
 *   3. **No recibir props.** La tienda sale de `useScopedStore()`.
 *
 * Si el archivo existe pero no cumple (2), se muestra un cartel que lo dice, no
 * una pantalla en blanco: un `undefined` como componente en React es un error
 * en consola y nada en el DOM, que es el peor de los dos mundos.
 */

import { lazy, type ComponentType, type LazyExoticComponent } from "react";

import { UnderConstruction, type ScreenBlueprint } from "./UnderConstruction";

/**
 * Mapa `ruta del modulo -> import dinamico`, resuelto por Vite al compilar.
 * `eager: false` (default) es lo que hace que cada pantalla sea un chunk propio.
 *
 * Las exclusiones no son un detalle: una pantalla que `routes.tsx` ya importa
 * de forma estatica NO es candidata de este mecanismo, y dejarla en el glob
 * hace que Rollup avise "dynamically imported but also statically imported" y
 * la meta igual en el bundle principal. Si alguna vez se agrega otro import
 * estatico de pantalla, va excluido aca tambien.
 */
const SCREEN_MODULES: Record<string, () => Promise<unknown>> = import.meta.glob([
  "../screens/*.tsx",
  "!../screens/StoreOverviewScreen.tsx",
  "!../screens/ProfitabilityScreen.tsx",
]);

/**
 * Elegir el componente exportado por el modulo.
 *
 * Acepta `default` o el export nombrado igual que el archivo. Cualquier otra
 * cosa (o nada) devuelve `null` y termina en el cartel de "no-export".
 */
function pickComponent(mod: unknown, moduleName: string): ComponentType | null {
  if (typeof mod !== "object" || mod === null) return null;
  const exports = mod as Record<string, unknown>;
  const candidate = exports["default"] ?? exports[moduleName];
  // El cast es la unica mentira de este archivo, y esta acotada: el modulo se
  // resuelve en runtime, asi que TypeScript no puede saber su forma. El
  // contrato "sin props" esta documentado arriba y en el cartel del placeholder.
  return typeof candidate === "function" ? (candidate as ComponentType) : null;
}

/**
 * `lazy()` no se puede llamar en render (crearia un componente nuevo cada vez y
 * remontaria la pantalla en cada re-render), asi que el resultado se cachea por
 * modulo.
 */
const CACHE = new Map<string, LazyExoticComponent<ComponentType>>();

export function screenComponent(
  blueprint: ScreenBlueprint,
): LazyExoticComponent<ComponentType> {
  const cached = CACHE.get(blueprint.module);
  if (cached !== undefined) return cached;

  const loader = SCREEN_MODULES[`../screens/${blueprint.module}.tsx`];

  const component = lazy<ComponentType>(async () => {
    if (loader === undefined) {
      return { default: () => <UnderConstruction blueprint={blueprint} reason="missing" /> };
    }
    const found = pickComponent(await loader(), blueprint.module);
    return {
      default:
        found ?? (() => <UnderConstruction blueprint={blueprint} reason="no-export" />),
    };
  });

  CACHE.set(blueprint.module, component);
  return component;
}
